import { type ConversationRecipient } from '@apps/web/components/shared/messages/conversation_recipient_picker';
import { getApiClient } from '@apps/web/lib/api_client';

/**
 * Sends the first message of a conversation that does not exist yet. The API
 * creates the thread and the message in one request, so both the new-message
 * dialog and the inbox's draft pane go through here rather than the ordinary
 * per-conversation send. Returns the new conversation's id and throws the
 * ApiError through, so each caller can report the failure in its own shape.
 */
export async function createConversationMessage({
    organizationId,
    recipient,
    content,
    englishSource,
    attachmentIds,
}: {
    readonly organizationId: string;
    readonly recipient: ConversationRecipient;
    readonly content: string;
    readonly englishSource: string | null;
    readonly attachmentIds: ReadonlyArray<string>;
}): Promise<string> {
    const requestBody =
        recipient.kind === 'caregiver'
            ? {
                  caregiver_id: recipient.caregiver.id,
                  content,
                  english_source: englishSource ?? undefined,
              }
            : {
                  phone_number:
                      recipient.kind === 'client'
                          ? recipient.client.phoneNumber
                          : recipient.phoneNumber,
                  content,
                  ...(recipient.kind === 'client'
                      ? {
                            client_recipient: {
                                client_id: recipient.client.id,
                                recipient_key: recipient.client.recipientKey,
                            },
                        }
                      : {}),
              };
    const response = await getApiClient().post({
        url: '/organizations/{organization_id}/conversations/messages',
        pathParams: {
            organization_id: organizationId,
        },
        body: {
            ...requestBody,
            ...(attachmentIds.length
                ? { attachment_ids: [...attachmentIds] }
                : {}),
        },
    });
    return response.conversation_id;
}
